import { useState, useEffect, useCallback } from "react";
import { Settings, Save, ToggleLeft, ToggleRight } from "lucide-react";
import api from "../../services/api";

function formatNumber(n) {
  if (n == null) return "0";
  return n.toLocaleString("pt-BR");
}

export default function DoletasConfig() {
  var configState = useState(null);
  var config = configState[0];
  var setConfig = configState[1];
  var loadingState = useState(true);
  var savingState = useState(false);

  var fetchConfig = useCallback(async function () {
    try {
      var res = await api.get("/doletas/config");
      setConfig(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      loadingState[1](false);
    }
  }, []);

  useEffect(function () { fetchConfig(); }, [fetchConfig]);

  async function handleSave() {
    savingState[1](true);
    try {
      var res = await api.put("/doletas/config", config);
      setConfig(res.data);
      alert("Configuracao salva com sucesso!");
    } catch (err) {
      alert("Erro ao salvar: " + (err.response?.data?.detail || err.message));
    } finally {
      savingState[1](false);
    }
  }

  function updateRate(key, value) {
    var rates = Object.assign({}, config.rates);
    rates[key] = parseFloat(value) || 0;
    setConfig(Object.assign({}, config, { rates: rates }));
  }

  if (loadingState[0] || !config) {
    return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" /></div>;
  }

  var rates = config.rates || {};
  var rateItems = [
    { key: "consorcio", label: "Consorcio", desc: "Cada R$1 = X doletas" },
    { key: "carta_contemplada", label: "Carta Contemplada", desc: "Cada R$1 = X doletas" },
    { key: "seguro_vida", label: "Seguro Vida", desc: "Cada R$1 = X doletas (validade 1 ano)" },
    { key: "seguro_residencial", label: "Seguro Residencial", desc: "Cada R$1 = X doletas (validade 1 ano)" },
    { key: "seguro_auto", label: "Seguro Automovel", desc: "Cada R$1 = X doletas (validade 1 ano)" },
    { key: "plano_saude", label: "Plano de Saude", desc: "Cada R$1 = X doletas (validade 1 ano)" },
  ];

  return (
    <div className="space-y-6" data-testid="doletas-config-panel">
      {/* Toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#0A2F3D]">Programa DOLETAS</h3>
            <p className="text-sm text-gray-500 mt-1">Ative ou desative o programa de fidelidade</p>
          </div>
          <button
            onClick={function () { setConfig(Object.assign({}, config, { enabled: !config.enabled })); }}
            className="flex items-center gap-2" data-testid="toggle-doletas"
          >
            {config.enabled ? (
              <ToggleRight className="w-10 h-10 text-emerald-500" />
            ) : (
              <ToggleLeft className="w-10 h-10 text-gray-300" />
            )}
            <span className={"text-sm font-medium " + (config.enabled ? "text-emerald-600" : "text-gray-400")}>
              {config.enabled ? "Ativo" : "Desativado"}
            </span>
          </button>
        </div>
      </div>

      {/* Rates */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-[#0A2F3D] mb-4">Taxas de Conversao por Produto</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rateItems.map(function (item) {
            var val = rates[item.key] || 0;
            var exampleValue = 100000;
            var exampleDoletas = Math.floor(exampleValue * val);
            var exampleReais = (exampleDoletas / (config.doletas_per_real || 100)).toFixed(2);
            return (
              <div key={item.key} className="border border-gray-100 rounded-xl p-4 bg-gray-50">
                <label className="text-sm font-medium text-[#0A2F3D] block mb-1">{item.label}</label>
                <p className="text-xs text-gray-400 mb-2">{item.desc}</p>
                <input
                  type="number" step="0.01" min="0" value={val}
                  onChange={function (e) { updateRate(item.key, e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2"
                  data-testid={"rate-" + item.key}
                />
                <p className="text-xs text-gray-500">Ex: R$ {formatNumber(exampleValue)} = {formatNumber(exampleDoletas)} doletas = R$ {exampleReais}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Multipliers */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-[#0A2F3D] mb-4">Multiplicadores de Vendedores</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="text-sm font-medium text-[#0A2F3D] block mb-1">Cross-Segmento</label>
            <p className="text-xs text-gray-400 mb-2">Indicacao para outro segmento</p>
            <input
              type="number" step="0.1" min="0" value={config.seller_cross_multiplier || 1.5}
              onChange={function (e) { setConfig(Object.assign({}, config, { seller_cross_multiplier: parseFloat(e.target.value) || 1.5 })); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="multiplier-cross"
            />
          </div>
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="text-sm font-medium text-[#0A2F3D] block mb-1">Proprio Segmento</label>
            <p className="text-xs text-gray-400 mb-2">Venda do proprio produto</p>
            <input
              type="number" step="0.05" min="0" value={config.seller_own_multiplier || 0.25}
              onChange={function (e) { setConfig(Object.assign({}, config, { seller_own_multiplier: parseFloat(e.target.value) || 0.25 })); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="multiplier-own"
            />
          </div>
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="text-sm font-medium text-[#0A2F3D] block mb-1">Meta Anual Vendedor</label>
            <p className="text-xs text-gray-400 mb-2">Doletas para premio especial</p>
            <input
              type="number" step="100000" min="0" value={config.seller_annual_goal || 2000000}
              onChange={function (e) { setConfig(Object.assign({}, config, { seller_annual_goal: parseInt(e.target.value) || 2000000 })); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="seller-annual-goal"
            />
          </div>
        </div>
      </div>

      {/* General Config */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-semibold text-[#0A2F3D] mb-4">Configuracoes Gerais</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="text-sm font-medium text-[#0A2F3D] block mb-1">Doletas por R$ 1,00</label>
            <input
              type="number" min="1" value={config.doletas_per_real || 100}
              onChange={function (e) { setConfig(Object.assign({}, config, { doletas_per_real: parseInt(e.target.value) || 100 })); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="doletas-per-real"
            />
          </div>
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="text-sm font-medium text-[#0A2F3D] block mb-1">Penalidade Doacao (%)</label>
            <p className="text-xs text-gray-400 mb-2">Quanto fica em doacao para terceiros</p>
            <input
              type="number" step="0.05" min="0" max="1" value={config.donation_penalty || 0.5}
              onChange={function (e) { setConfig(Object.assign({}, config, { donation_penalty: parseFloat(e.target.value) || 0.5 })); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="donation-penalty"
            />
          </div>
          <div className="border border-gray-100 rounded-xl p-4 bg-gray-50">
            <label className="text-sm font-medium text-[#0A2F3D] block mb-1">Validade (dias)</label>
            <input
              type="number" min="30" value={config.expiry_days || 365}
              onChange={function (e) { setConfig(Object.assign({}, config, { expiry_days: parseInt(e.target.value) || 365 })); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              data-testid="expiry-days"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={savingState[0]}
          className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-xl font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
          data-testid="btn-save-config">
          <Save className="w-4 h-4" />{savingState[0] ? "Salvando..." : "Salvar Configuracao"}
        </button>
      </div>
    </div>
  );
}
